import * as React from 'react';
import { Map, List, Set, is } from 'immutable';
import cx from 'classnames';
import identity from 'lodash/identity';
import { RecordCellSet } from 'domain/journal/helper';

import mediaQuery from 'lib/mediaQuery';

import Autocomplete from 'components/Tables/Cells/ListCell';
import PaginationAutocomplete from 'components/Tables/Cells/ListCell/PaginationAutocomplete';
import { findText } from 'components/Tables/Cells/ListCell/helpers';
import HintInput from 'components/Tables/Cells/ListCell/hintInput';
import LiteralCell from 'components/Tables/Cells/LiteralCell';
import {
  textMapValueNormalize,
  keyCodeCharacter,
  keyCodeArrow,
  filterList,
  toggleDate,
} from 'components/Tables/helpers';
import { executeFunc } from 'lib/helpers';
import { hotkeyMatcher } from 'lib/hotkeysHelpers';
import elements from 'components/elements';
import { cellFsm, statesDict, EVENTS, STATE, GROUP } from 'components/Tables/fsm';
import CellRenderer from 'components/Tables/Cells/CellRenderer';
import BooleanCell from 'components/Tables/Cells/BooleanCell';
import DateToggle from 'components/Tables/Cells/Cell/DateToggle';
import Tooltip from 'components/mui/Tooltip';

import constants from 'components/Tables/constatnt';

import { HotkeyCellListFactory, HotkeyMatchFactory } from 'domain/documents/factories';
import { CellRoot, CellToggleButton, CellRendererValue } from 'components/Tables/Cells/Cell/StyledComponents';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

function getDefaultState(data) {
  if (data.type === 'checkbox') return STATE.booleanPassive;
  if (data.readonly) return STATE.roPassive;
  // eslint-disable-next-line no-use-before-define
  if (data.name === Cell.dateToggleCellName) return STATE.datePassive;
  if (data.type === 'index_list') return STATE.selectPassive;
  if (data.type === 'paginated_list') return STATE.selectPassive;
  if (data.type === 'paginated_multiselect_list') return STATE.selectPassive;
  if (data.type === 'reconciliation') return STATE.reconciliationPassive;
  if (data.type === 'string_auto_generated') return STATE.textareaPassive;
  /* if (['string', 'string_auto_generated'].includes(data.type)) {
    return STATE.passive;
  } */
  return STATE.passive;
}

class Cell extends React.PureComponent {
  static getGroup(currentState, group) {
    return STATE[currentState.name].group === group;
  }

  static disableHintForCell(name) {
    return name === Cell.dateToggleCellName;
  }

  static dateToggleCellName = 'priv_date';

  constructor(props) {
    super(props);
    this.state = {
      value: this.stateValue,
      isOpen: false,
      currentState: getDefaultState(props.data),
    };
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.data.name !== this.props.data.name) {
      const currentState = getDefaultState(nextProps.data);

      this.fsm = cellFsm(statesDict, this.defState(nextProps.data));
      this.setState({ currentState });
    }
  }

  componentDidUpdate(prevProps) {
    const { data, active } = this.props;
    if (active === data._cell && prevProps.active !== data._cell) {
      this.setFocus();
    }
  }

  get listItem() {
    const {
      cellSet: { value, display },
    } = this.props.data;
    return display || value || null;
  }

  get stateValue() {
    const { cellSet } = this.props.data;
    return cellSet;
  }

  get value() {
    const {
      data: { type },
    } = this.props;
    if (['index_list', 'paginated_list', 'paginated_multiselect_list'].includes(type)) return this.listItem;
    return this.stateValue.value;
  }

  get hint() {
    const {
      data: { hint },
    } = this.props;
    return hint || this.value;
  }

  get celleStyle() {
    return this.props.data.style.toJS();
  }

  get cellClasses() {
    // TODO: not sure if these classes are used at all
    return cx({
      'je-cell-has-error': !this.isValid(),
      'je-cell-disabled': this.isDisabled(),
    });
  }

  defState = (data) => getDefaultState(data || this.props.data);

  getInputMaxLength = () => {
    const {
      data: { maxLength = constants.INPUT_DEFAULT_SIZE },
    } = this.props;

    return maxLength;
  };

  setFocus = () => {
    if (this.field && typeof this.field.focus === 'function') {
      this.field.focus();
    }
  };

  setFCMState = (nextState, cb) => {
    this.setState(
      {
        currentState: nextState,
      },
      () => {
        executeFunc(cb);
      },
    );
  };

  eventCollector = (event, params) => {
    const state = this.fsm();
    const nextState = this.fsm(event);
    const { setEditing, endEditing, isJEEditing } = this.props;

    if (['insert', 'edit', 'update'].some((name) => nextState.name.toLowerCase().includes(name))) {
      if (!isJEEditing) {
        setEditing();
      }
    } else if (isJEEditing && event === EVENTS.onFocus) {
      endEditing();
    }

    if (state.name !== nextState.name) {
      if (params && params.persist && typeof params.persist === 'function') {
        params.persist();
      }
      nextState.action.call(this, params);
    }
  };

  handleHotkey = (hotkeyValue) => {
    const { onChange, data } = this.props;

    const hotkeyCellListItem = HotkeyCellListFactory({
      name: data.name,
      cell: data._cell,
      set: data.cellSet,
    });

    const hotkeyMatchData = HotkeyMatchFactory({
      key: hotkeyValue,
      cell_list: List([hotkeyCellListItem]),
    });

    onChange(data._cell, data.cellSet, false, hotkeyMatchData);
  };

  handleDateToggler = () => {
    this.eventCollector(EVENTS.onDateToggleClick, this.props.data._cell);
  };

  handleChange = (e, ...arg) => {
    this.setState(
      {
        value: this.state.value.merge(e.target),
      },
      () => {
        if (arg.length) {
          executeFunc(arg[arg.length - 1]);
        }
      },
    );
  };

  handleBlur = () => {
    this.eventCollector(EVENTS.onBlur);
  };

  handleFocus = () => {
    this.eventCollector(EVENTS.onFocus, this.props.data._cell);
  };

  handleClick = () => {
    this.eventCollector(EVENTS.onClick, this.props.data._cell);
  };

  handleShieldClick = (state) => {
    this.eventCollector(EVENTS.onShieldClick, this.props.data._cell);
    setTimeout(() => {
      this.setState({ isOpen: state });
      if (!state) {
        this.handleBlur();
      }
    }, 100);
  };

  handleKey = (e) => {
    const { rtl, isPriorityErp, hotkeyList } = this.props;
    const LRKeyCodeArrow = Set([37, 39]);
    const { keyCode } = e;

    if (LRKeyCodeArrow.has(keyCode)) {
      this.eventCollector(EVENTS.LRArrowKey, keyCode);
    }
    // no handling for now
    // if (UDKeyCodeArrow.has(keyCode)) {
    //   this.eventCollector(EVENTS.UDArrowKey, keyCode);
    // }

    if (keyCodeArrow.has(keyCode)) {
      this.eventCollector(EVENTS.arrowKey, keyCode);
    }

    if (keyCode === 9) {
      this.eventCollector(EVENTS.arrowKey, rtl || isPriorityErp ? 37 : 39);
    }

    if (keyCodeCharacter.has(keyCode)) {
      if (!e.metaKey && !e.ctrlKey && !e.altKey) {
        this.eventCollector(EVENTS.charKey, keyCode);
      }
    }
    if (keyCode === 13) {
      // ENTER
      this.eventCollector(EVENTS.onEnter, e);
    }
    if (keyCode === 27) {
      // Esc
      this.eventCollector(EVENTS.onEsc);
    }
    if (keyCode === 8) {
      // BackSpase
      this.eventCollector(EVENTS.onBackSpase);
    }

    // hotkeys from b-end
    hotkeyMatcher(e.nativeEvent, hotkeyList, this.handleHotkey);
  };

  handleDoubleClick = () => {
    this.eventCollector(EVENTS.onDoubleClick);
    setTimeout(() => this.setState({ isOpen: true }), 100);
  };

  handleCreate = (cellSet, ...arg) => {
    // force call onChange for update JE, because update function have check on empty value
    this.props.onChange(this.props.data._cell, cellSet);

    this.setState(
      {
        value: cellSet,
      },
      () => {
        if (arg.length) executeFunc(arg[arg.length - 1]);
      },
    );
  };

  fsm = cellFsm(statesDict, this.defState());

  field;

  insert = (nextState, cb) => {
    const state = {
      currentState: nextState, // STATE.edit,
      value: RecordCellSet(),
    };

    this.setState(state, () => {
      executeFunc(cb);
    });
  };

  update = (nextState, cb) => {
    const {
      props: { data, isBulkEdit },
      state: { value },
    } = this;
    if (!is(data.cellSet, value) || isBulkEdit) {
      if ((typeof value.value === 'string' && value.value.length === 0) || !value.value) {
        this.props.onChange(data._cell, RecordCellSet());
      } else {
        this.props.onChange(data._cell, value);
      }
    }
    this.setState(
      {
        currentState: nextState,
      },
      () => {
        executeFunc(cb);
      },
    );
  };

  toggleDate = (nextState, cb) => {
    const {
      props: { data },
    } = this;
    const value = RecordCellSet({ value: toggleDate(data.cellSet.value) });
    this.props.onChange(data._cell, value);
    this.setState(
      {
        currentState: nextState,
      },
      () => {
        executeFunc(cb);
      },
    );
  };

  transition = (nextState, cb) => {
    if (this.state.currentState.group !== nextState.group) {
      this.setState(
        {
          currentState: nextState,
          value: this.stateValue,
        },
        () => {
          executeFunc(cb);
        },
      );
    } else {
      executeFunc(cb);
    }
  };

  isEmpty = () => !(this.value && this.value.length > 0);

  isValid = () => {
    const { data: cell } = this.props;
    return Boolean(cell.valid);
  };

  isDisabled = () => {
    // only cells with no inputs (cell.return = false) are marked readonly to disallow copying text
    const { data: cell } = this.props;
    return cell.readonly && !cell.return;
  };

  activeComponent;

  buttonRef = (el) => {
    this.field = el;
    if (this.props.data._cell === this.props.active) {
      this.setFocus();
    }
  };

  render() {
    const {
      data: { type, _cell, name, readonly, hint },
      setActiveCell,
      optionList,
      reference,
      hotkeyList,
    } = this.props;
    const { currentState, isOpen } = this.state;
    if (this.constructor.getGroup(currentState, GROUP.input)) {
      return (
        <CellRoot className={cx(this.cellClasses)}>
          <HintInput
            id={_cell}
            name={{ _cell, name }}
            style={this.celleStyle}
            rtl={this.props.rtl}
            valueNormalize={textMapValueNormalize}
            optionList={optionList}
            getRefs={(el) => {
              this.field = el;
            }}
            onComplete={() => this.eventCollector(EVENTS.onSelect)}
            filterList={filterList}
            maxLength={this.getInputMaxLength()}
            onDoubleClick={this.handleDoubleClick}
            type={type}
            input={{
              value: this.state.value,
              onChange: this.handleChange,
              onKeyDown: this.handleKey,
              onBlur: this.handleBlur,
            }}
          />
        </CellRoot>
      );
    }

    if (this.constructor.getGroup(currentState, GROUP.select)) {
      // eslint-disable-next-line max-len
      const finDocWidth = () => window.innerWidth - (window.innerHeight < mediaQuery.maxHeightNumber ? 400 : 600);
      const { props, state, handleChange, handleBlur, handleShieldClick, handleKey, handleCreate } = this;
      const { data, rtl, documentView } = props;
      const { includes, pageToken, _options, type, isCreatable, include_id } = data;
      const { isOpen, value } = state;

      const sharedProps = {
        id: _cell,
        name: { _cell, name },
        style: this.celleStyle,
        filterList: findText,
        valueNormalize: textMapValueNormalize,
        rtl,
        maxWidth: documentView.get('infoBarWidth') || finDocWidth(),
        getRefs: (el, comp) => {
          this.field = el;
          this.activeComponent = comp;
        },
        onComplete: () => this.eventCollector(EVENTS.onSelect),
        isOpen,
        params: { id: includes, cellName: name },
        pageLimit: 50,
        pageItemsCount: 10,
        pageToken,
        input: {
          value,
          onChange: handleChange,
          onBlur: handleBlur,
          onToggleOpen: handleShieldClick,
          onKeyDown: handleKey,
          isCreatable,
          onCreate: handleCreate,
        },
      };

      const listRenders = {
        index_list: () => (
          <CellRoot className={cx(this.cellClasses)}>
            <Autocomplete {...sharedProps} optionList={_options} />
          </CellRoot>
        ),
        paginated_list: () => (
          <CellRoot className={cx(this.cellClasses)}>
            <PaginationAutocomplete {...sharedProps} include_id={include_id} />
          </CellRoot>
        ),
        paginated_multiselect_list: () => (
          <CellRoot className={cx(this.cellClasses)}>
            <PaginationAutocomplete {...sharedProps} include_id={include_id} multiSelect />
          </CellRoot>
        ),
      };

      const listRender = listRenders[type] || identity;
      return listRender();
    }
    if (this.constructor.getGroup(currentState, GROUP.cell)) {
      const { link } = this.props.data;
      return (
        <CellRoot>
          <Tooltip t={!isOpen && (hint || this.value)} disableFocusListener>
            <CellRendererValue
              className={cx({
                [`je-cell-type-${type === 'float' ? type : 'string'}`]: true,
                'je-cell-disabled': this.isDisabled(),
                'je-cell-readonly': readonly,
              })}
              style={this.celleStyle}
              data-element={elements.je.readonly.container}
              data-element-id={`${_cell}-${type}`}
            >
              {link ? (
                <a href={link} target="_blank" rel="noopener noreferrer">
                  {this.value}
                </a>
              ) : (
                this.value
              )}
            </CellRendererValue>
          </Tooltip>
        </CellRoot>
      );
    }
    if (this.constructor.getGroup(currentState, GROUP.literal)) {
      return (
        <CellRoot>
          <LiteralCell
            maxLength={this.getInputMaxLength()}
            key={this.state.value.value}
            data={this.props.data}
            onBlur={this.handleBlur}
            getRefs={(el) => {
              this.field = el;
            }}
            onChange={this.props.onChange}
            eventCollector={this.eventCollector}
            value={this.state.value.value}
          />
        </CellRoot>
      );
    }
    if (this.constructor.getGroup(currentState, GROUP.boolean)) {
      return (
        <CellRoot>
          <BooleanCell
            data={this.props.data}
            onBlur={this.handleBlur}
            onFocus={this.handleFocus}
            onChange={this.props.onChange}
            onClick={this.handleClick}
            getRefs={(el) => {
              this.field = el;
            }}
            onKeyDown={this.handleKey}
          />
        </CellRoot>
      );
    }

    return (
      <CellRenderer
        data={this.props.data}
        maxLength={this.getInputMaxLength()}
        className={this.cellClasses}
        onChange={this.props.onChange}
        handleKey={this.handleKey}
        handleDoubleClick={this.handleDoubleClick}
        handleFocus={this.handleFocus}
        handleClick={this.handleClick}
        handleBlur={this.handleBlur}
        value={this.value}
        buttonRef={this.buttonRef}
        activeReference={this.props.activeReference}
        createRefernce={this.props.createRefernce}
        setActiveReference={this.props.setActiveReference}
        isSupport={this.props.isSupport}
        eventCollector={this.eventCollector}
        insertGrid={this.props.insertGrid}
        setActiveCell={setActiveCell}
        hintDisabled={this.constructor.disableHintForCell(name)}
        reference={reference}
        hotkeyList={hotkeyList}
      >
        {this.constructor.getGroup(currentState, GROUP.selectPassive) && (
          <CellToggleButton
            onMouseUp={() => this.handleShieldClick(true)}
            data-element={elements.je.autocomplete.shield}
          >
            <ArrowDropDownIcon />
          </CellToggleButton>
        )}
        {this.constructor.getGroup(currentState, GROUP.datePassive) && (
          <DateToggle data={this.props.data} onClick={() => this.handleDateToggler()} />
        )}
      </CellRenderer>
    );
  }
}

export default Cell;
